import graphql__86a003cddd9c58cdd212 from "./__generated__/UploadBankStatementDialogMutation.graphql.ts";import * as React from "react";
import {useState} from "react";
import {graphql} from "react-relay";
import {useRelayEnvironment} from "react-relay/hooks";
import {Link} from "react-router";
import {useIntl} from "react-intl";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSpinnerThird} from "@fortawesome/pro-regular-svg-icons";
import {commitMutationWithErrorHandling} from "../../helper/commitMutationWithErrorHandling";
import {formatISO} from "../../helper/date";
import {formatAmount} from "../../helper/revenueFormat";
import {useErrorContext} from "../../contexts/ErrorContext";
import type {UploadBankStatementDialogMutation, UploadBankStatementDialogMutation$data} from "./__generated__/UploadBankStatementDialogMutation.graphql";
import {formatDay} from "../../helper/dateFormat";

type Props = {
    bankAccount: {
        bankAccountIdentifier: string
        name: string
    }
    onClose: (changed: boolean) => void
}

const uploadBankStatementMutation = graphql__86a003cddd9c58cdd212;

/**
 * Uploads a CSV statement to a bank account and shows what the upload came to. The server reads
 * the file in memory and keeps only daily totals, so there is nothing to look at but this result.
 */
const UploadBankStatementDialog: React.FC<Props> = ({bankAccount, onClose}) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();
    const [file, setFile] = useState<File | null>(null);
    const [coveredUntil, setCoveredUntil] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [result, setResult] = useState<UploadBankStatementDialogMutation$data['uploadBankStatement'] | null>(null);

    const upload = () => {
        if (!file) {
            return;
        }
        setIsUploading(true);
        setErrorMessage(null);

        const fileReader = new FileReader();
        fileReader.onerror = () => {
            setIsUploading(false);
            setErrorMessage(intl.formatMessage({id: 'dialog.UploadBankStatementDialog.unreadable'}));
        };
        fileReader.onload = () => {
            commitMutationWithErrorHandling<UploadBankStatementDialogMutation>(
                relayEnvironment,
                {
                    mutation: uploadBankStatementMutation,
                    variables: {
                        input: {
                            id: bankAccount.bankAccountIdentifier,
                            coveredUntil: coveredUntil === '' ? null : coveredUntil,
                            upload: {
                                filename: file.name,
                                data: fileReader.result as string,
                                lastModifiedAt: formatISO(new Date(file.lastModified)),
                            },
                        },
                    },
                    onCompleted: (response, errors) => {
                        setIsUploading(false);
                        if (errors && errors.length > 0) {
                            setErrorMessage(errors[0].message);
                            return;
                        }
                        setResult(response.uploadBankStatement);
                    },
                    onError: error => {
                        setIsUploading(false);
                        setErrorMessage(error.message);
                    },
                },
                addError,
                {suppressGlobalErrorHandling: true}
            );
        };
        fileReader.readAsDataURL(file);
    };

    return (
        <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-500 opacity-75"/>
                </div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <div
                    className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6" role="dialog" aria-modal="true" aria-labelledby="dialog-headline"
                >
                    <div className="bg-gray-100 px-10 py-8 -m-6">
                        <h2 className="text-xl font-medium text-gray-900">
                            {intl.formatMessage({id: 'dialog.UploadBankStatementDialog.headline'}, {name: bankAccount.name})}
                        </h2>
                    </div>
                    <div className="pt-8 mt-6 px-4 space-y-4">
                        {result ? (
                            <dl className="text-sm grid grid-cols-2 gap-y-2">
                                <dt className="text-gray-500">{intl.formatMessage({id: 'dialog.UploadBankStatementDialog.result.period'})}</dt>
                                <dd className="text-gray-900 text-right">{formatDay(intl, result.firstBookingDate)} – {formatDay(intl, result.lastBookingDate)}</dd>
                                <dt className="text-gray-500">{intl.formatMessage({id: 'dialog.UploadBankStatementDialog.result.coveredUntil'})}</dt>
                                <dd className="text-gray-900 text-right">{formatDay(intl, result.coveredUntil)}</dd>
                                <dt className="text-gray-500">{intl.formatMessage({id: 'dialog.UploadBankStatementDialog.result.bookingCount'})}</dt>
                                <dd className="text-gray-900 text-right">{intl.formatNumber(result.bookingCount)}</dd>
                                <dt className="text-gray-500">{intl.formatMessage({id: 'dialog.UploadBankStatementDialog.result.balance'}, {date: formatDay(intl, result.lastBookingDate)})}</dt>
                                <dd className="text-gray-900 text-right font-medium">{formatAmount(result.balanceAtLastBookingDate)}</dd>
                                <dt className="text-gray-500">{intl.formatMessage({id: 'dialog.UploadBankStatementDialog.result.otherShare'})}</dt>
                                <dd className="text-gray-900 text-right">
                                    {result.otherShareOfOutgoing === null || result.otherShareOfOutgoing === undefined
                                        ? '–'
                                        : intl.formatNumber(result.otherShareOfOutgoing, {style: 'percent', maximumFractionDigits: 0})}
                                    <div>
                                        <Link className="text-blue-600 hover:underline" to="/configuration/finance#rules" onClick={() => onClose(true)}>
                                            {intl.formatMessage({id: 'dialog.UploadBankStatementDialog.result.adjustRules'})}
                                        </Link>
                                    </div>
                                </dd>
                            </dl>
                        ) : (
                            <>
                                <p className="text-sm text-gray-500">{intl.formatMessage({id: 'dialog.UploadBankStatementDialog.description'})}</p>
                                <input
                                    type="file"
                                    accept=".csv,text/csv"
                                    disabled={isUploading}
                                    onChange={event => setFile(event.target.files?.[0] ?? null)}
                                    className="block w-full text-sm text-gray-700"
                                />
                                <label className="block text-sm text-gray-700">
                                    {intl.formatMessage({id: 'dialog.UploadBankStatementDialog.coveredUntil'})}
                                    <input
                                        type="date"
                                        value={coveredUntil}
                                        disabled={isUploading}
                                        onChange={event => setCoveredUntil(event.target.value)}
                                        className="mt-1 block w-full rounded-md border border-gray-300 text-sm text-gray-700"
                                    />
                                </label>
                                <p className="text-xs text-gray-500">{intl.formatMessage({id: 'dialog.UploadBankStatementDialog.coveredUntil.hint'})}</p>
                                {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
                            </>
                        )}
                    </div>
                    <div className="shrink-0 px-4 border-t border-gray-200 py-5 mt-6 sm:px-6">
                        <div className="space-x-3 flex justify-end">
                            {result ? (
                                <button
                                    type="button"
                                    onClick={() => onClose(true)}
                                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-xs text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                                >
                                    {intl.formatMessage({id: 'button.close'})}
                                </button>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => onClose(false)}
                                        className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-xs text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                    >
                                        {intl.formatMessage({id: 'button.cancel'})}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={upload}
                                        disabled={!file || isUploading}
                                        className={"inline-flex justify-center py-2 px-4 border border-transparent shadow-xs text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" + (!file || isUploading ? " opacity-50 cursor-not-allowed" : "")}
                                    >
                                        {isUploading && <FontAwesomeIcon icon={faSpinnerThird} spin className="h-5 w-5 mr-2"/>}
                                        {intl.formatMessage({id: 'dialog.UploadBankStatementDialog.submit'})}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UploadBankStatementDialog;
